import { Box, CircularProgress, Grid } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import dayjs, { Dayjs } from 'dayjs';
import { ReactElement, useEffect, useState } from 'react';
import { Controller, ControllerRenderProps, FieldValues, Path, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { ReactComponent as InfoIconBlue } from '../../../assets/icons/info-blue.svg';
import {
    DEFAULT_CURRENCY_SYMBOL,
    EWS_DURATION_MAP,
    iwsGoalTypeMapper,
    STATUSVARIANTS,
    TermTabType,
    FEATURE_NAMES
} from '../../../common/constants';
import {
    convertDateFormat,
    convertDateFormatReverse,
    getHeaderIconFromType,
    getYYYYMMDDfromDate
} from '../../../common/helper';
import { useAppDispatch, useAppSelector } from '../../../common/hooks';
import { GoalTypeProps } from '../../../common/types/clients-entity';
import { ErrorSpan } from '../../../containers/retirement-goals-drawer';
import { setEnableConfirmConsoleNavigation } from '../../../features/client';
import { createGoal, getGoalCategory, saveGoal } from '../../../features/client-goals';
import { selectAffectedEwsTerms, setAffectedEwsTerms } from '../../../features/ews';
import { updatePlanState } from '../../../features/global/globalSlice';
import {
    deepEqual,
    checkFFEnabled,
    formatCurrency,
    getAffectedEwsTerms,
    removeNonNumeric,
    getClientJourneyFlowType
} from '../../../utils';
import { BasicDatePicker, ComboSelect, TextboxInput, Typography } from '../../atoms';
import { DrawerComp, InfoTooltip } from '../../molecules';
import { GoalType } from '../../molecules/GoalType/GoalType';
import { GoalCategoryType } from '../../../common/types';

export interface FormField<T extends FieldValues = any> {
    name: Path<T>;
    label: string;
    type: 'text' | 'number' | 'date' | 'select' | 'currency';
    placeholder?: string;
    required?: boolean;
    validation?: {
        pattern?: RegExp;
        // eslint-disable-next-line no-unused-vars
        validate?: (value: any, formValues?: T) => boolean | string;
        min?: number;
        max?: number;
    };
    options?: Array<{ label: string; value: string }>;
    infoTooltip?: React.ReactNode;
    // eslint-disable-next-line no-unused-vars
    customRender?: (props: {
        field: ControllerRenderProps<T, Path<T>>;
        // eslint-disable-next-line no-unused-vars
        error?: any;
        // eslint-disable-next-line no-unused-vars
        onChange?: (value: any) => void;
        // eslint-disable-next-line no-unused-vars
        watch?: (name?: Path<T>) => any;
    }) => ReactElement;
    HelperTextComponent?: () => ReactElement;
    colspan?: 1 | 2;
    // eslint-disable-next-line no-unused-vars
    isMMYYYY?: (watch?: (name?: Path<T>) => any) => boolean;
    // eslint-disable-next-line no-unused-vars
    isYYYY?: (watch?: (name?: Path<T>) => any) => boolean;
    // eslint-disable-next-line no-unused-vars
    isEnabled?: (watch?: (name?: Path<T>) => any) => boolean;
    // eslint-disable-next-line no-unused-vars
    dependencyFields?: Array<Path<T>>;
    // eslint-disable-next-line no-unused-vars
    disableValidation?: boolean;
    // eslint-disable-next-line no-unused-vars
    onFieldChange?: (
        // eslint-disable-next-line no-unused-vars
        value: any,
        // eslint-disable-next-line no-unused-vars
        setValue: (name: Path<T>, value: any, options?: any) => void,
        // eslint-disable-next-line no-unused-vars
        clearErrors?: (name?: Path<T> | Path<T>[]) => void
    ) => void;
    // eslint-disable-next-line no-unused-vars
    datePickerStartDate?: (watch?: (name?: Path<T>) => any) => string | undefined;
}
export interface ConfigurableDrawerProps<T extends FieldValues> {
    open: boolean;
    // eslint-disable-next-line no-unused-vars
    handleClose: (flag: boolean) => void;
    heading?: string;
    subHeading?: string;
    selectedGoal?: boolean | GoalTypeProps;
    goalData?: any;
    goalType: string;
    fields: FormField<T>[];
    defaultValues: T;
    onSubmit?: () => void;
    drawerLabel?: string;
    additionalFields?: () => Record<string, any>;
}

export const ItemContainer = styled('div')(({ theme, colspan }: { theme: any; colspan: 1 | 2 }) => ({
    display: 'flex',
    // justifyContent: 'center',
    flexDirection: 'column',
    gap: '8px',
    alignItems: 'flex-start',
    width: '100%',
    gridColumn: `span ${colspan}`,
    position: 'relative',
    '& a': {
        color: theme.palette.background.paper
    }
}));

export const DateErrorSpan = styled(Typography)(({ theme }) => ({
    color: theme.palette.berry[400],
    // position: 'absolute',
    top: '100%',
    left: 0,
    width: 'calc(200% + 16px)',
    lineHeight: 1.57,
    letterSpacing: 0.00714,
    marginTop: '5px',
    zIndex: 1,
    fontStyle: 'italic'
}));

export const Footer = styled('div')(() => ({
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    flexDirection: 'column',
    fontSize: '10px',
    height: '100%'
}));

export const ItemContainerSplit = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    gap: '8px',
    '& a': {
        color: theme.palette.background.paper
    }
}));

const GoalFormWrapper = styled('form')(() => ({
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    fontSize: '14px',
    paddingBottom: '30px'
}));

export const SavedFundsWrapper = styled('div')(() => ({
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column'
}));

export const Wrap = styled('div')(() => ({
    position: 'relative'
}));

export const StyledInfoText = styled(Typography)(({ theme }) => ({
    color: theme.palette.grey[400],
    fontSize: '16px',
    fontStyle: 'normal',
    lineHeight: '20px',
    fontWeight: 600
}));

const GoalCategoryInfoBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    gridColumn: 'span 2',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    borderRadius: '4px',
    backgroundColor: theme.palette.info[50],
    border: `1px solid ${theme.palette.info[400]}`,
    '& svg': {
        flexShrink: 0,
        width: '20px',
        height: '20px'
    }
}));

export const ConfigurableDrawer = <T extends FieldValues>({
    handleClose,
    selectedGoal,
    open,
    goalData,
    goalType,
    fields,
    defaultValues,
    drawerLabel,
    additionalFields
}: ConfigurableDrawerProps<T>) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const {
        orgInfo,
        globalConfig: { currencySymbol }
    } = useAppSelector((state) => state.global);
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const isFlow2 = getClientJourneyFlowType(adviceClient?.tenant) === 'flow2';
    const adviceGoals = useAppSelector((state) => state.adviceGoals || []);
    const affectedEwsTerms = useAppSelector(selectAffectedEwsTerms);
    const [apiErrorToken, setApiErrorToken] = useState(0);
    const isRetirementGoal =
        goalData?.type === iwsGoalTypeMapper.retirement ||
        (typeof selectedGoal !== 'boolean' && selectedGoal?.type === iwsGoalTypeMapper.retirement);

    const originalGoalsData: typeof adviceGoals = JSON.parse(JSON.stringify(adviceGoals));

    const {
        control,
        formState: { errors, isValid },
        trigger,
        reset,
        getValues,
        setValue,
        watch,
        setError,
        clearErrors
    } = useForm<T>({
        mode: 'all',
        reValidateMode: 'onChange',
        defaultValues: defaultValues as any
    });
    const [goalCategory, setGoalCategory] = useState<string | null>(
        isRetirementGoal ? EWS_DURATION_MAP.longTerm : null
    );
    const [goalCategoryOrder, setGoalCategoryOrder] = useState<number | null>(
        isRetirementGoal ? TermTabType.LONG + 1 : null
    );
    const [goalCategoryLoading, setGoalCategoryLoading] = useState<boolean>(false);
    const language = useAppSelector((state) => state?.global?.currentLanguage);

    const fetchGoalCategory = async (targetDate: string | Dayjs, startDate: string | Dayjs | null = null) => {
        let goalObj: GoalCategoryType = {
            goalName: 'currentGoal',
            goalTargetDate: dayjs(targetDate).format('DD-MM-YYYY')
        };
        if (!isRetirementGoal && targetDate) {
            if (startDate) {
                goalObj = {
                    ...goalObj,
                    goalStartDate: dayjs(startDate).format('DD-MM-YYYY')
                };
            }
            const data = {
                language: language || 'en',
                goals: [goalObj]
            };
            try {
                setGoalCategoryLoading(true);
                const response = await getGoalCategory(data);
                if (response && response.goalCategoryResult && response.goalCategoryResult.currentGoal) {
                    setGoalCategory(response.goalCategoryResult.currentGoal.value);
                    setGoalCategoryOrder(response.goalCategoryResult.currentGoal.order);
                }
            } catch (error) {
                console.error('Error fetching goal category:', error);
            } finally {
                setGoalCategoryLoading(false);
            }
        }
    };

    const submitFunds = async () => {
        try {
            const data = getValues();
            await trigger();

            if (!goalData && isValid && adviceClient?._id && typeof selectedGoal !== 'boolean') {
                const isDuplicateName = adviceGoals?.formFields
                    .filter((goal) => (typeof selectedGoal !== 'boolean' ? goal.goalId !== goalData?.goalId : true))
                    .some((i) => i.data.goalName?.toLowerCase() === (data as any).goalName?.toLowerCase());

                if (isDuplicateName) {
                    setError('goalName' as Path<T>, { message: t('DUPLICATE_NAME') });
                    return;
                } else {
                    clearErrors('goalName' as Path<T>);
                }

                const generatedId = uuidv4();
                const formData: any = { ...data, goalType };

                if (typeof selectedGoal !== 'boolean') {
                    formData.wealthGoalType = goalData ? goalData?.title : selectedGoal?.title;
                }

                formData.sources = {
                    incomeSource: [],
                    investmentSource: []
                };

                // Format currency and date fields
                fields.forEach((field) => {
                    if (field.type === 'currency' && formData[field.name]) {
                        formData[field.name] = Number(removeNonNumeric(formData[field.name]));
                    }
                    if (field.type === 'date' && formData[field.name]) {
                        formData[field.name] = getYYYYMMDDfromDate(formData[field.name]);
                    }
                });

                const nextIndex = !adviceGoals?.formFields ? 0 : adviceGoals.formFields.length + 1;
                let additionalData = additionalFields ? additionalFields() : {};
                Object.assign(formData, additionalData);
                const newFormField = {
                    goalId: generatedId,
                    data: {
                        ...formData,
                        index: nextIndex,
                        ...(checkFFEnabled(FEATURE_NAMES['CONTINUITY_OF_ADVICE_FE_AEX-3603']) &&
                            isFlow2 && {
                                lastRecommendedPortfolioId: null
                            }),
                        // Send goalId on FE for FF-disabled, and for non-flow2 (Advice) when FF is enabled
                        ...((checkFFEnabled(FEATURE_NAMES['AEX-3590_3604_NEW_FLOW_UPDATE_SAVE_PLAN']) || !isFlow2) && {
                            goalId: generatedId
                        }),
                        // Mark as newly created on FE so the goal is never tagged with an `update` flag in the payload
                        ...(checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']) &&
                            !isFlow2 && {
                                isNew: true
                            }),
                        // createdAt is sent only when FF is disabled
                        ...(!checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']) && {
                            createdAt: new Date().toISOString()
                        }),
                        type: selectedGoal && typeof selectedGoal !== 'boolean' ? selectedGoal?.type : goalData?.type,
                        goalCategory: isRetirementGoal ? EWS_DURATION_MAP.longTerm : goalCategory,
                        goalCategoryOrder: isRetirementGoal ? TermTabType.LONG + 1 : goalCategoryOrder
                    }
                };

                let originalGoalsDataClone: typeof adviceGoals = JSON.parse(JSON.stringify(adviceGoals));
                const updatedGoalsData = Object.assign({}, originalGoalsDataClone);
                updatedGoalsData.formFields = updatedGoalsData?.formFields
                    ? [...updatedGoalsData?.formFields, newFormField]
                    : [newFormField];
                if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                    updatedGoalsData.formFields.forEach((item) => {
                        if (item.goalId !== generatedId && !item.data['isNew'] && item.data['update'] !== true) {
                            item.data['update'] = false;
                        }
                    });
                }
                updatedGoalsData.domainEmail = orgInfo?.orgEmail ?? '';
                updatedGoalsData.goeConfigEmail = orgInfo?.orgEmail ?? '';
                updatedGoalsData.riskProfile = adviceClient.riskProfile;
                updatedGoalsData.assignedClientId = adviceClient._id;
                updatedGoalsData.error = false;
                updatedGoalsData.loading = false;

                if (updatedGoalsData.global?.hasRunPipe) {
                    updatedGoalsData.global = {
                        ...updatedGoalsData.global,
                        status: STATUSVARIANTS.INCOMPLETE
                    };
                }
                if (updatedGoalsData.global?.enablePDFGenerate) {
                    updatedGoalsData.global.enablePDFGenerate = false;
                }
                if (updatedGoalsData.global?.enablePlanDetails) {
                    updatedGoalsData.global.enablePlanDetails = false;
                }

                const newGoalCategory = isRetirementGoal ? EWS_DURATION_MAP.longTerm : goalCategory;
                dispatch(setAffectedEwsTerms(getAffectedEwsTerms(affectedEwsTerms, newGoalCategory)));
                !updatedGoalsData._id
                    ? await dispatch(createGoal(updatedGoalsData)).unwrap()
                    : await dispatch(saveGoal(updatedGoalsData));

                if (adviceGoals?.global?.isPlanRun) {
                    dispatch(setEnableConfirmConsoleNavigation(true));
                }

                reset();
                dispatch(updatePlanState({ key: 'isPlanOpen', value: true }));
                handleClose(false);
            } else if (typeof selectedGoal !== 'boolean' && goalData) {
                handleEditClick();
            }
        } catch (error) {
            console.error('Error in creating goals', error);
            setApiErrorToken((currentToken) => currentToken + 1);
        }
    };

    const handleEditClick = async () => {
        await trigger();
        try {
            if (isValid) {
                const data = getValues();
                const isDuplicateName = adviceGoals?.formFields
                    .filter((goal) => (typeof selectedGoal !== 'boolean' ? goal.goalId !== goalData?.goalId : true))
                    .some((i) => i.data.goalName?.toLowerCase() === (data as any).goalName?.toLowerCase());

                if (isDuplicateName) {
                    setError('goalName' as Path<T>, { message: t('DUPLICATE_NAME') });
                    return;
                } else {
                    clearErrors('goalName' as Path<T>);
                }

                let originalGoalsDataClone: typeof adviceGoals = JSON.parse(JSON.stringify(adviceGoals));
                const updatedGoalsData = Object.assign({}, originalGoalsDataClone);

                updatedGoalsData?.formFields.forEach((item) => {
                    const itemData = item['data'];
                    if (item.goalId === goalData?.goalId) {
                        // Update all fields
                        fields.forEach((field) => {
                            const fieldValue = (data as any)[field.name];
                            if (field.type === 'currency' && fieldValue) {
                                itemData[field.name] = Number(removeNonNumeric(fieldValue));
                            } else if (field.type === 'date' && fieldValue) {
                                itemData[field.name] =
                                    typeof fieldValue === 'string'
                                        ? convertDateFormatReverse(fieldValue)
                                        : getYYYYMMDDfromDate(fieldValue);
                            } else {
                                itemData[field.name] = fieldValue;
                            }
                        });

                        itemData['type'] =
                            selectedGoal && typeof selectedGoal !== 'boolean' ? selectedGoal?.type : goalData?.type;
                        itemData['goalCategory'] = isRetirementGoal ? EWS_DURATION_MAP.longTerm : goalCategory;
                        itemData['goalCategoryOrder'] = isRetirementGoal ? TermTabType.LONG + 1 : goalCategoryOrder;

                        let additionalData = additionalFields ? additionalFields() : {};
                        for (const key in additionalData) {
                            itemData[key] = additionalData[key];
                        }
                        if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                            itemData['update'] = true;
                        } else {
                            itemData['updatedAt'] = new Date().toISOString();
                        }
                    } else if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                        itemData['update'] = false;
                    }
                });

                if (goalData && !deepEqual(originalGoalsData.formFields, updatedGoalsData.formFields)) {
                    if (updatedGoalsData.global.hasRunPipe) {
                        updatedGoalsData.global.status = STATUSVARIANTS.INCOMPLETE;
                    }
                    if (updatedGoalsData.global?.enablePDFGenerate) {
                        updatedGoalsData.global.enablePDFGenerate = false;
                    }
                    if (updatedGoalsData.global?.enablePlanDetails) {
                        updatedGoalsData.global.enablePlanDetails = false;
                    }

                    const oldCategory = goalData?.goalCategory;
                    const newCategoryValue = isRetirementGoal ? EWS_DURATION_MAP.longTerm : goalCategory;
                    dispatch(setAffectedEwsTerms(getAffectedEwsTerms(affectedEwsTerms, newCategoryValue, oldCategory)));

                    await dispatch(saveGoal(updatedGoalsData));
                }

                reset();
                dispatch(updatePlanState({ key: 'isPlanOpen', value: true }));
                handleClose(false);
            }
        } catch (error) {
            console.error('Error in editing goals', error);
            setApiErrorToken((currentToken) => currentToken + 1);
        }
    };

    useEffect(() => {
        if (goalData) {
            const resetData: any = { ...defaultValues };
            fields.forEach((field) => {
                if (field.type === 'date' && goalData[field.name]) {
                    resetData[field.name] = convertDateFormat(goalData[field.name]);
                } else if (goalData[field.name] !== undefined) {
                    resetData[field.name] = goalData[field.name];
                }
            });

            reset(resetData as T);
        }
    }, []);

    useEffect(() => {
        if (open) {
            if (isRetirementGoal) {
                setGoalCategory(EWS_DURATION_MAP.longTerm);
                setGoalCategoryOrder(TermTabType.LONG + 1);
            } else if (goalData?.goalCategory) {
                setGoalCategory(goalData?.goalCategory);
                setGoalCategoryOrder(goalData?.goalCategoryOrder);
            }
        } else {
            setGoalCategory(null);
            setGoalCategoryOrder(null);
        }
    }, [open, isRetirementGoal]);

    // Watch all date type fields
    useEffect(() => {
        const dateFields = fields.filter((field) => field.type === 'date');
        if (dateFields.length === 0) return;
        const subscription = watch(async (value, { name, type }) => {
            if (type === 'change' && name) {
                const changedField = dateFields.find((field) => field.name === name);
                const isEndDateField = name === 'targetDate';
                if (changedField) {
                    const dateValue = value[name as keyof typeof value];
                    if (dateValue && dayjs(dateValue).isValid()) {
                        let dateFieldsValidObj: Record<string, boolean> = {};
                        for (const field of dateFields) {
                            const fieldValue = value[field.name as keyof typeof value];
                            if (fieldValue && dayjs(fieldValue).isValid()) {
                                dateFieldsValidObj[field.name] = await trigger(field.name);
                            }
                        }
                        if (isEndDateField && dateFieldsValidObj?.targetDate) {
                            fetchGoalCategory(dateValue);
                            return;
                        }
                    }
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [fields, watch, trigger]);

    const checkIfFormIsEdited = () => {
        let currentGoal = goalData ? JSON.parse(JSON.stringify(goalData)) : JSON.parse(JSON.stringify(defaultValues));
        let data = watch();

        if (currentGoal?.title) delete currentGoal['title'];
        if (currentGoal?.goalType) {
            delete currentGoal.isIWSApplied;
            delete currentGoal.riskProfile;
            delete currentGoal.title;
            delete currentGoal.accounts;
            if (currentGoal?.recommendedWealthSplit != undefined) delete currentGoal.recommendedWealthSplit;
            (data as any).goalType = currentGoal.goalType;
        }

        const compareDataWithCurrentGoal = (data: any, currentGoal: any) => {
            if (!data || !currentGoal) return false;
            return Object.keys(data).every((key) => {
                return JSON.stringify(data[key]) === JSON.stringify(currentGoal[key]);
            });
        };

        return !compareDataWithCurrentGoal(data, currentGoal);
    };

    const isSubmitDisabled = () => {
        if (!isValid || Object.entries(errors)?.length !== 0 || goalCategoryLoading) return true;
        return false;
    };

    const handleDrawerClose = () => {
        reset();
        handleClose(false);
    };

    const renderField = (field: FormField<T>) => {
        const fieldName = field.name as Path<T>;
        const colspan = field.colspan || 2;

        return (
            <ItemContainer key={fieldName} theme={theme} colspan={colspan}>
                <Grid container alignItems="center">
                    <Typography
                        variant="labels.large"
                        sx={{ color: theme.palette.neutral[400] }}
                        label={`${field.label}${field.required ? ' *' : ''}`}
                    />
                    {field.infoTooltip && <InfoTooltip message={field.infoTooltip} />}
                </Grid>
                <Controller
                    name={fieldName}
                    control={control}
                    rules={{
                        required:
                            field.required && !field.validation?.validate
                                ? {
                                      value: true,
                                      message: t('TEXT_THIS_IS_REQUIRED_FIELD')
                                  }
                                : false,
                        ...(field.validation?.pattern && {
                            pattern: {
                                value: field.validation.pattern,
                                message: t('TEXT_FULLNAME_VALIDATION')
                            }
                        }),
                        ...(field.validation?.validate && {
                            validate: field.validation.validate
                        })
                    }}
                    render={({ field: controllerField }) => {
                        if (field.customRender) {
                            return field.customRender({
                                field: controllerField,
                                error: errors[fieldName],
                                onChange: (value) => setValue(fieldName, value, { shouldValidate: true }),
                                watch: watch
                            });
                        }
                        let fieldComponent = [];
                        switch (field.type) {
                            case 'text':
                            case 'number':
                                fieldComponent.push(
                                    <TextboxInput
                                        onBlur={controllerField.onBlur}
                                        onChange={controllerField.onChange}
                                        value={controllerField.value}
                                        inputRef={controllerField.ref}
                                        size="small"
                                        error={!!errors[fieldName]}
                                        type={field.type}
                                        data-testid={fieldName}
                                        placeholder={field.placeholder || field.label}
                                        fullWidth
                                    />
                                );
                                break;

                            case 'currency':
                                fieldComponent.push(
                                    <TextboxInput
                                        onBlur={controllerField.onBlur}
                                        onChange={(e) => {
                                            const numericValue = Number(removeNonNumeric(e.target.value));
                                            setValue(fieldName, (isNaN(numericValue) ? 0 : numericValue) as any, {
                                                shouldValidate: true,
                                                shouldDirty: true
                                            });
                                        }}
                                        value={controllerField.value ? formatCurrency(controllerField.value, true) : ''}
                                        inputRef={controllerField.ref}
                                        size="small"
                                        error={!!errors[fieldName]}
                                        type="text"
                                        data-testid={fieldName}
                                        fullWidth
                                        placeholder={field.placeholder || currencySymbol || DEFAULT_CURRENCY_SYMBOL}
                                        iconPlacement="start"
                                    />
                                );
                                break;

                            case 'date':
                                fieldComponent.push(
                                    <BasicDatePicker
                                        enabled={field.isEnabled ? field.isEnabled(watch) : true}
                                        type="single"
                                        startDate={
                                            (field.datePickerStartDate && field.datePickerStartDate(watch)) ||
                                            dayjs().startOf('month').format()
                                        }
                                        value={controllerField.value}
                                        onChange={async (value: string | Dayjs | null) => {
                                            if (field.isYYYY || field.isMMYYYY) {
                                                value = dayjs(value).startOf('month');
                                            }
                                            controllerField.onChange(value);
                                            if (fieldName === 'targetDate') setGoalCategory(null);
                                        }}
                                        onBlur={controllerField.onBlur}
                                        data-testid={fieldName}
                                        // minErrorMsg={t('TEXT_VALID_FUTURE_DATE')}
                                        isMmYyyy={field.isMMYYYY && field.isMMYYYY(watch)}
                                        isYyyy={field.isYYYY && field.isYYYY(watch)}
                                        hasError={!!errors[fieldName]}
                                    />
                                );
                                break;

                            case 'select':
                                fieldComponent.push(
                                    <ComboSelect
                                        {...controllerField}
                                        onSelectChange={(value: any) => {
                                            setValue(fieldName, value, { shouldValidate: true });
                                            if (!isRetirementGoal && field.onFieldChange) {
                                                field.onFieldChange?.(value, setValue, clearErrors);
                                                setGoalCategory(null);
                                            }
                                        }}
                                        size="small"
                                        selectedValue={controllerField.value ?? ''}
                                        options={field.options || []}
                                        data-testid={fieldName}
                                        placeholder={field.placeholder}
                                    />
                                );
                                break;

                            default:
                                fieldComponent.push(<></>);
                        }

                        if (fieldComponent.length > 0 && field?.HelperTextComponent) {
                            fieldComponent.push(field.HelperTextComponent());
                        }

                        return <>{fieldComponent}</>;
                    }}
                />
                {errors[fieldName] &&
                    !field?.disableValidation &&
                    (field.type === 'date' ? (
                        <DateErrorSpan variant="subtitle2" role="alert" label={(errors[fieldName] as any)?.message} />
                    ) : (
                        <ErrorSpan variant="subtitle2" role="alert" label={(errors[fieldName] as any)?.message} />
                    ))}
            </ItemContainer>
        );
    };

    return (
        <DrawerComp
            open={open}
            buttonlabel={t('COMPLETE_TEXT')}
            header={t(
                goalData ? goalData?.drawerTitle : typeof selectedGoal !== 'boolean' ? selectedGoal?.drawerTitle : ''
            )}
            drawerIcon={
                <GoalType
                    type={
                        getHeaderIconFromType(
                            goalData
                                ? goalData?.title
                                : typeof selectedGoal !== 'boolean'
                                ? selectedGoal?.title
                                : 'Default'
                        )!
                    }
                />
            }
            handleClose={handleDrawerClose}
            onSubmit={submitFunds}
            disableSubmit={isSubmitDisabled()}
            hasChange={checkIfFormIsEdited()}
            heapId={`complete-${goalData?.title || goalType.toLowerCase()}-goals-drawer`}
            APIError={apiErrorToken}
        >
            <GoalFormWrapper>
                <StyledInfoText>{drawerLabel}</StyledInfoText>
                {fields.map((field) => renderField(field))}
                {goalCategory && !goalCategoryLoading && (
                    <GoalCategoryInfoBox>
                        <InfoIconBlue />
                        <Typography variant="labels.large" sx={{ color: theme.palette.neutral.black }}>
                            <Trans
                                i18nKey={isRetirementGoal ? 'RET_GOAL_TERM_INFO_TEXT' : 'GOAL_TERM_INFO_TEXT'}
                                values={{ goalCategory: t(goalCategory.split(' ')[0].toUpperCase()) }}
                                components={{ strong: <strong /> }}
                            />
                        </Typography>
                    </GoalCategoryInfoBox>
                )}
                {goalCategoryLoading && (
                    <GoalCategoryInfoBox sx={{ justifyContent: 'center' }}>
                        <CircularProgress size={20} />
                    </GoalCategoryInfoBox>
                )}
            </GoalFormWrapper>
        </DrawerComp>
    );
};
